import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Menu, X } from "lucide-react";
import { FIRM } from "@/data/firm";
import NavDropdown, { NavDropdownMobile } from "@/components/NavDropdown";

// Grouped top-nav — three lightweight drop-downs + a plain Legal Links link.
// Grouping cuts visible items from 9 → 4 and mirrors how top-tier firms
// (Skadden, Wachtell, Kirkland) organise nav.
const NAV_GROUPS = [
  {
    label: "The Firm",
    testId: "firm",
    items: [
      { to: "/about", label: "About" },
      { to: "/people", label: "People" },
      { to: "/join", label: "Careers" },
    ],
  },
  {
    label: "Practice",
    testId: "practice",
    items: [
      { to: "/services", label: "Practice Areas" },
      { to: "/industries", label: "Industries" },
    ],
  },
  {
    label: "Export Control Tools",
    testId: "export-tools",
    items: [
      { to: "/ccl", label: "CCL Navigator" },
      { to: "/usml", label: "USML Navigator" },
      { to: "/tools/license-check", label: "EAR License Checker" },
      { to: "/tools/ear-license-exceptions", label: "EAR License Exceptions" },
      { to: "/tools/itar-license-exemptions", label: "ITAR License Exemptions" },
    ],
  },
  {
    label: "Resources",
    testId: "resources",
    items: [
      { to: "/blog", label: "Insights" },
      { to: "/news", label: "News" },
      { to: "/resources", label: "Guides" },
    ],
  },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <header
      className="sticky top-0 z-40 bg-ivory/95 backdrop-blur-sm border-b border-hairline"
      data-testid="site-header"
    >
      {/* Contact strip */}
      <div className="hidden md:block bg-ink text-ivory">
        <div className="max-w-7xl mx-auto px-8 py-2.5 flex items-center justify-end text-[11px] tracking-[0.18em] uppercase">
          <a href={FIRM.emailHref} className="hover:text-brass-light transition-colors normal-case tracking-wide" data-testid="header-email-link">{FIRM.email}</a>
        </div>
      </div>

      {/* Main bar */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6 flex items-center justify-between">
        <Link to="/" className="flex flex-col leading-none" data-testid="logo-link">
          <span className="font-serif text-xl sm:text-[1.6rem] text-ink tracking-tight leading-none">Shelton Law</span>
          <span className="text-[10px] uppercase tracking-[0.3em] text-brass-deep mt-2">PLLC · Attorneys at Law</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-6 xl:gap-9">
          {NAV_GROUPS.map((g) => (
            <NavDropdown key={g.testId} label={g.label} items={g.items} testId={g.testId} />
          ))}
          <NavLink
            to="/legal-links"
            className={({ isActive }) =>
              `whitespace-nowrap text-[11px] tracking-[0.22em] uppercase font-semibold transition-colors ${
                isActive ? "text-brass-deep" : "text-ink hover:text-brass"
              }`
            }
            data-testid="nav-legal-links"
          >
            Legal Links
          </NavLink>
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/contact")}
            className="hidden md:inline-flex items-center bg-ink text-ivory px-6 py-3 text-[11px] tracking-[0.22em] uppercase font-semibold hover:bg-brass transition-colors"
            data-testid="header-cta-button"
          >
            Contact
          </button>
          <button
            className="lg:hidden p-2 text-ink"
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden border-t border-hairline bg-ivory max-h-[80vh] overflow-y-auto">
          <div className="px-6 py-5 flex flex-col gap-1">
            {NAV_GROUPS.map((g) => (
              <NavDropdownMobile
                key={g.testId}
                label={g.label}
                items={g.items}
                onNavigate={() => setOpen(false)}
              />
            ))}
            <Link
              to="/legal-links"
              onClick={() => setOpen(false)}
              className="py-2.5 text-ink hover:text-brass border-b border-hairline-soft text-[13px] tracking-[0.22em] uppercase font-semibold"
              data-testid="mobile-nav-legal-links"
            >
              Legal Links
            </Link>
            <div className="pt-4 text-sm space-y-1.5">
              <a href={FIRM.emailHref} className="block text-ink" data-testid="mobile-email-link">{FIRM.email}</a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
